import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, FileDown, ArrowRightLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { API } from "@/config";
import { ItemsEditor, emptyItem } from "./ItemsEditor";

const STATUSES = ["draft", "sent", "accepted", "rejected"];
const INTRA = (state) => ["uttar pradesh", "up", "09"].includes((state || "").trim().toLowerCase());

export const QuotationsSection = () => {
  const [quotations, setQuotations] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [customer, setCustomer] = useState({ name: "", address: "", state: "Uttar Pradesh", gstin: "" });
  const [items, setItems] = useState([emptyItem()]);
  const [validUntil, setValidUntil] = useState("");
  const [notes, setNotes] = useState("");

  const fetchQuotations = async () => {
    const { data } = await axios.get(`${API}/quotations`, { withCredentials: true });
    setQuotations(data);
  };

  useEffect(() => {
    fetchQuotations();
  }, []);

  const totals = useMemo(() => {
    let subtotal = 0, tax = 0;
    items.forEach((it) => {
      const amt = (it.qty || 0) * (it.rate || 0);
      subtotal += amt;
      tax += (amt * (it.gst_rate || 0)) / 100;
    });
    const intra = INTRA(customer.state);
    return {
      subtotal, cgst: intra ? tax / 2 : 0, sgst: intra ? tax / 2 : 0,
      igst: intra ? 0 : tax, total: subtotal + tax,
    };
  }, [items, customer.state]);

  const createQuotation = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(
        `${API}/quotations`,
        {
          customer,
          items: items.filter((it) => it.description.trim()),
          valid_until: validUntil,
          notes,
          status: "sent",
        },
        { withCredentials: true }
      );
      toast.success(`Quotation ${data.quote_no} created`);
      setShowAdd(false);
      setCustomer({ name: "", address: "", state: "Uttar Pradesh", gstin: "" });
      setItems([emptyItem()]);
      setValidUntil("");
      setNotes("");
      fetchQuotations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create quotation");
    }
  };

  const setStatus = async (id, status) => {
    await axios.patch(`${API}/quotations/${id}`, { status }, { withCredentials: true });
    toast.success("Status updated");
    fetchQuotations();
  };

  const convert = async (id) => {
    try {
      const { data } = await axios.post(`${API}/quotations/${id}/convert`, {}, { withCredentials: true });
      toast.success(`Converted to invoice ${data.invoice_no}`);
      fetchQuotations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Conversion failed");
    }
  };

  const deleteQuotation = async (id) => {
    await axios.delete(`${API}/quotations/${id}`, { withCredentials: true });
    toast.success("Quotation deleted");
    fetchQuotations();
  };

  return (
    <div data-testid="quotations-section">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{quotations.length} quotations</p>
        <Button size="sm" className="gap-1" data-testid="add-quotation-button" onClick={() => setShowAdd(true)}>
          <Plus className="h-4 w-4" /> New Quotation
        </Button>
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-border bg-card shadow-sm">
        <Table data-testid="quotations-table">
          <TableHeader>
            <TableRow>
              <TableHead>Quote No</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Items</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {quotations.map((q) => (
              <TableRow key={q.id} data-testid={`quotation-row-${q.id}`}>
                <TableCell className="font-medium">{q.quote_no}</TableCell>
                <TableCell>{q.date}</TableCell>
                <TableCell>{q.customer.name}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {q.items.length} items ({q.items.filter((i) => i.kind === "product").length}P / {q.items.filter((i) => i.kind === "service").length}S)
                </TableCell>
                <TableCell className="font-semibold">₹{q.total}</TableCell>
                <TableCell>
                  <Select value={q.status} onValueChange={(v) => setStatus(q.id, v)}>
                    <SelectTrigger className="w-32" data-testid={`quotation-status-${q.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1"
                      data-testid={`quotation-pdf-${q.id}`}
                      onClick={() => window.open(`${API}/quotations/${q.id}/pdf`, "_blank")}
                    >
                      <FileDown className="h-3.5 w-3.5" /> PDF
                    </Button>
                    {q.status !== "accepted" && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1"
                        data-testid={`quotation-convert-${q.id}`}
                        onClick={() => convert(q.id)}
                      >
                        <ArrowRightLeft className="h-3.5 w-3.5" /> To Invoice
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" data-testid={`quotation-delete-${q.id}`} onClick={() => deleteQuotation(q.id)} aria-label="Delete quotation">
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-quotation-dialog" className="max-h-[90vh] max-w-3xl overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading">New Quotation (Products + Services)</DialogTitle>
          </DialogHeader>
          <form onSubmit={createQuotation} className="grid gap-4" data-testid="add-quotation-form">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Customer Name *</Label>
                <Input required value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} data-testid="quotation-customer-name" />
              </div>
              <div className="grid gap-2">
                <Label>Customer GSTIN</Label>
                <Input value={customer.gstin} onChange={(e) => setCustomer({ ...customer, gstin: e.target.value })} data-testid="quotation-customer-gstin" />
              </div>
              <div className="grid gap-2">
                <Label>Address</Label>
                <Input value={customer.address} onChange={(e) => setCustomer({ ...customer, address: e.target.value })} data-testid="quotation-customer-address" />
              </div>
              <div className="grid gap-2">
                <Label>State *</Label>
                <Input required value={customer.state} onChange={(e) => setCustomer({ ...customer, state: e.target.value })} data-testid="quotation-customer-state" />
              </div>
              <div className="grid gap-2">
                <Label>Valid Until</Label>
                <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} data-testid="quotation-valid-until" />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Items (Products & Services) *</Label>
              <ItemsEditor items={items} setItems={setItems} showKind />
            </div>
            <div className="grid gap-2">
              <Label>Terms & Notes</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="quotation-notes" />
            </div>
            <div className="rounded-xl bg-muted/50 p-4 text-sm" data-testid="quotation-totals-preview">
              <div className="flex justify-between"><span>Subtotal</span><span>₹{totals.subtotal.toFixed(2)}</span></div>
              {totals.igst > 0 ? (
                <div className="flex justify-between"><span>IGST</span><span>₹{totals.igst.toFixed(2)}</span></div>
              ) : (
                <>
                  <div className="flex justify-between"><span>CGST</span><span>₹{totals.cgst.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span>SGST</span><span>₹{totals.sgst.toFixed(2)}</span></div>
                </>
              )}
              <div className="mt-1 flex justify-between border-t border-border pt-1 font-bold">
                <span>Grand Total</span><span>₹{totals.total.toFixed(2)}</span>
              </div>
            </div>
            <Button type="submit" data-testid="quotation-submit-button">Create Quotation</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
